/**
 * The Notes card on a contact's Details tab - readable AND editable in place.
 *
 * It used to be read-only: an empty contact said "No notes added" with no
 * way to add one short of opening the full edit panel. Notes are free prose,
 * so they edit where they are read: "Add note" / "Edit" flips the card into a
 * textarea, and Save writes ONLY `notes` through the existing client update
 * (`PUT /clients/<uuid>`), so nothing else on the record is touched.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPencilLight, PiPlusLight, PiTextAlignLeftLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { clientActions } from '../../store/actions/ClientActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import GlassCard from '../shared/brand/GlassCard';
import Button from '../shared/TailwindComponents/Button';
import Textarea from '../shared/TailwindComponents/Textarea';
import logger from '../../utils/logger';

const NOTES_MAX = 5000;

interface ContactNotesCardProps {
  clientUuid?: string;
  notes?: string | null;
  /** Called with the saved notes so the page can merge them into its record. */
  onSaved?: (notes: string) => void;
}

const ContactNotesCard: React.FC<ContactNotesCardProps> = ({ clientUuid, notes, onSaved }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(notes || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraft(notes || '');
  }, [notes, isEditing]);

  const hasNotes = Boolean(notes && notes.trim());
  const canEdit = Boolean(clientUuid);

  const handleCancel = () => {
    setDraft(notes || '');
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!clientUuid) return;
    // Plain text, rendered as a React text node - no HTML escaping, which
    // would double-encode `&` and quotes on the next read.
    const next = draft.trim().slice(0, NOTES_MAX);
    if (next === (notes || '').trim()) {
      setIsEditing(false);
      return;
    }
    try {
      setIsSaving(true);
      await clientActions.updateClient(clientUuid, { notes: next });
      onSaved?.(next);
      setIsEditing(false);
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'success',
        message: t('Contact updated successfully'),
        duration: 3000,
      }));
    } catch (error: any) {
      logger.error('Failed to save contact notes', { message: error?.message });
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: t('Failed to save note'),
        duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const headerAction = !isEditing && canEdit ? (
    <Button
      variant="outline"
      size="small"
      onClick={() => setIsEditing(true)}
      icon={hasNotes ? <PiPencilLight className="h-3.5 w-3.5" /> : <PiPlusLight className="h-3.5 w-3.5" />}
    >
      {hasNotes ? t('Edit') : t('Add note')}
    </Button>
  ) : undefined;

  return (
    <GlassCard icon={<PiTextAlignLeftLight />} title={t('Notes')} actions={headerAction}>
      {isEditing ? (
        <div className="space-y-3">
          <Textarea
            id="contact-notes-inline"
            label={t('Notes')}
            placeholder={t('Add a note...')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={5}
            className="w-full"
          />
          <div className="flex justify-end gap-2">
            <Button variant="neutral" size="small" onClick={handleCancel} disabled={isSaving}>
              {t('Cancel')}
            </Button>
            <Button variant="primary" size="small" onClick={handleSave} isLoading={isSaving} disabled={isSaving}>
              {t('Save')}
            </Button>
          </div>
        </div>
      ) : hasNotes ? (
        <p className="text-sm text-fg-body leading-relaxed whitespace-pre-wrap max-w-prose">{notes}</p>
      ) : (
        <p className="text-sm text-fg-muted">{t('No notes added')}</p>
      )}
    </GlassCard>
  );
};

export default ContactNotesCard;
